import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import Select from "react-select";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAtom } from "jotai";
import { gbState, fYear } from "../../../components/shared/GlobalState";
import { FaSave, FaArrowLeft } from "react-icons/fa";

const AssetInwardChild = () => {
  const navigate = useNavigate();
  const [assetData, setAssetData] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [{ errorLogApi, user }] = useAtom(gbState);
  const [financialYear] = useAtom(fYear);
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    asset_id: null,
    item_code: null,
    asset_tag: null,
    quantity: null,
    inward_type: null,
    inward_rate: null,
    inward_description: null,
    comments: null,
  });

  const handleChange = ({ currentTarget: input }) => {
    // Clear error when user starts typing
    if (errors[input.name]) {
      setErrors({
        ...errors,
        [input.name]: null
      });
    }
    
    setFormData({
      ...formData,
      [input.name]: input.value,
    });
  };

  const selectStyles = {
    control: (base, state) => ({
      ...base,
      border: state.isFocused ? "1px solid #3b82f6" : "1px solid #d1d5db",
      boxShadow: state.isFocused ? "0 0 0 2px rgba(59, 130, 246, 0.25)" : "none",
      borderRadius: "0.375rem",
      padding: "2px",
      fontSize: "0.875rem",
      transition: "all 0.2s",
      "&:hover": {
        borderColor: state.isFocused ? "#3b82f6" : "#9ca3af",
      },
    }),
    menu: (base) => ({
      ...base,
      zIndex: 9999,
      boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
      borderRadius: "0.375rem",
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected ? "#3b82f6" : state.isFocused ? "#dbeafe" : "white",
      color: state.isSelected ? "white" : "#374151",
      fontSize: "0.875rem",
      padding: "8px 12px",
    }),
    placeholder: (base) => ({
      ...base,
      fontSize: "0.875rem",
      color: "#9ca3af",
    }),
  };
  
  const validateForm = () => {
    const newErrors = {};
    if (!formData.asset_id) {
      newErrors.asset_id = "Please select an asset";
    }
    if (!formData.inward_type) {
      newErrors.inward_type = "Please select inward type";
    }
    if (!formData.quantity) {
      newErrors.quantity = "Quantity is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => {
    const getDataFromServer = () => {
      let userToken = localStorage.getItem("token");

      let urlLink = `${process.env.REACT_APP_API_URL}/asset/${user.church_id}`;

      const config = {
        method: "GET",
        url: urlLink,
        headers: {
          Authorization: `Bearer ${userToken}`,
        },
      };
      axios(config)
        .then(function (response) {
          //console.log(JSON.stringify(response.data.users));
          const oldData = response.data.assets;
          const updatedData = oldData.map((data) => {
            return {
              ...data,
              value: data?._id,
              label: data?.asset_name + " (" + data?.item_code + ")",
              item_code: data?.item_code,
            };
          });

          setAssetData(updatedData);
        })
        .catch(function (err) {
          console.log("error api call ", err);
        });
    };

    getDataFromServer();
  }, []);

  const handleAssetAddition = async () => {
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    try {
      const userToken = localStorage.getItem("token");
      const response = await axios({
        method: "post",
        url: `${process.env.REACT_APP_API_URL}/church/inward/add`,
        headers: {
          Authorization: `Bearer ${userToken}`,
        },
        data: { ...formData, financial_year: financialYear?.label },
      });
      
      toast.success("Asset inward recorded successfully!");
      navigate("/assets/inventory");
    } catch (error) {
      const errorMessage = error?.response?.data?.message || "Failed to add asset inward";
      toast.error(typeof errorMessage === 'object' ? JSON.stringify(errorMessage) : errorMessage);
      errorLogApi(error, "AssetInwardChild", "/church/inward/add");
    } finally {
      setIsSubmitting(false);
    }
  };

  const dropDownChange = (selected, dropdown) => {
    // Clear error when user selects an option
    if (errors[dropdown.name]) {
      setErrors({
        ...errors,
        [dropdown.name]: null
      });
    }
    
    setFormData({
      ...formData,
      [dropdown.name]: selected.value,
    });
  };

  const inwardOptions = [
    { value: 1, label: "Purchase" },
    { value: 2, label: "Donation" },
    { value: 3, label: "Transferred" },
  ];

  // Common styles
  const inputStyle = `w-full p-3 font-sans text-sm border border-gray-300 rounded-md outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200`;
  const buttonBaseStyle = `flex items-center justify-center gap-2 px-4 py-2 text-white rounded-md transition-all duration-200 font-medium`;

  return (
    <div className="flex flex-col w-full bg-gray-50 min-h-screen">
      <div className="p-6 w-full max-w-4xl mx-auto">
        {/* Header with title and actions */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
          <h1 className="text-2xl font-bold text-gray-800">Add Asset Inward</h1>
          
          <div className="flex gap-3">
            <button
              className={`${buttonBaseStyle} bg-gray-600 hover:bg-gray-700`}
              onClick={() => navigate(-1)}
              disabled={isSubmitting}
            >
              <FaArrowLeft size={16} />
              <span>Back</span>
            </button>
            
            <button
              className={`${buttonBaseStyle} ${isSubmitting ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`}
              onClick={handleAssetAddition}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <FaSave size={16} />
                  <span>Save Inward</span>
                </>
              )}
            </button>
          </div>
        </div>
        
        {/* Main content */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="p-6">
            <form onSubmit={(e) => { e.preventDefault(); handleAssetAddition(); }}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Item/Asset Name <span className="text-red-500">*</span>
                  </label>
                  <Select
                    name="asset_id"
                    onChange={(selected, dropdown) => {
                      const selectedAsset = assetData.find(asset => asset.value === selected.value);
                      // Clear error when user selects an option
                      if (errors.asset_id) {
                        setErrors({
                          ...errors,
                          asset_id: null
                        });
                      }
                      setFormData({
                        ...formData,
                        [dropdown.name]: selected.value,
                        item_code: selectedAsset?.item_code || ""
                      });
                    }}
                    options={assetData}
                    placeholder="Select Asset from List"
                    styles={selectStyles}
                    menuPortalTarget={document.body}
                    className={errors.asset_id ? 'border-red-500 ring-1 ring-red-500' : ''}
                  />
                  {errors.asset_id && (
                    <p className="mt-1 text-sm text-red-500">{errors.asset_id}</p>
                  )}
                </div>
                
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Item Code
                  </label>
                  <input
                    type="text"
                    name="item_code"
                    value={formData?.item_code || ""}
                    disabled
                    className={`${inputStyle} bg-gray-100 cursor-not-allowed`}
                  />
                </div>
                
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Asset Tag
                  </label>
                  <input
                    type="text"
                    name="asset_tag"
                    value={formData.asset_tag || ""}
                    onChange={handleChange}
                    placeholder="Enter asset tag"
                    className={inputStyle}
                  />
                </div>
                
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Inward Type <span className="text-red-500">*</span>
                  </label>
                  <Select
                    name="inward_type"
                    onChange={(selected, dropdown) => {
                      // Clear error when user selects an option
                      if (errors.inward_type) {
                        setErrors({
                          ...errors,
                          inward_type: null
                        });
                      }
                      setFormData({
                        ...formData,
                        [dropdown.name]: selected.value,
                      });
                    }}
                    options={inwardOptions}
                    placeholder="Select Inward type"
                    styles={selectStyles}
                    menuPortalTarget={document.body}
                    className={errors.inward_type ? 'border-red-500 ring-1 ring-red-500' : ''}
                  />
                  {errors.inward_type && (
                    <p className="mt-1 text-sm text-red-500">{errors.inward_type}</p>
                  )}
                </div>
                
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Inward Quantity <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="quantity"
                    value={formData.quantity || ""}
                    onChange={handleChange}
                    placeholder="Enter quantity"
                    className={`${inputStyle} ${errors.quantity ? 'border-red-500 ring-1 ring-red-500' : ''}`}
                  />
                  {errors.quantity && (
                    <p className="mt-1 text-sm text-red-500">{errors.quantity}</p>
                  )}
                </div>
                
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Inward Rate
                  </label>
                  <input
                    type="number"
                    name="inward_rate"
                    value={formData.inward_rate || ""}
                    onChange={handleChange}
                    placeholder="Enter rate"
                    className={inputStyle}
                  />
                </div>
                
                <div className="md:col-span-2">
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Description
                  </label>
                  <textarea
                    name="inward_description"
                    value={formData.inward_description || ""}
                    onChange={handleChange}
                    rows="4"
                    placeholder="Enter description (optional)"
                    className={inputStyle}
                  />
                </div>
              </div>
            </form>
          </div>
        </div>
        
        {/* Helpful tips */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-md p-4">
          <h3 className="text-sm font-medium text-blue-800 mb-2">Tips:</h3>
          <ul className="text-sm text-blue-700 list-disc pl-5 space-y-1">
            <li>Select an existing asset from the dropdown</li>
            <li>Specify the inward type (Purchase, Donation, or Transferred)</li>
            <li>Enter the quantity of assets being added to inventory</li>
            <li>Include rate information for purchased assets</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AssetInwardChild;
